import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { ExternalCharacterListItem } from '@cartwhl/common/api/getExternalCharacters';
import Text from '@cartwhl/ui/base/Text';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import FileIcon from '@cartwhl/ui/icons/FileIcon';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import Button from '@cartwhl/ui/input/Button';
import FilesList, {
  type FilesListFile,
  type FilesListHandle,
} from '@cartwhl/ui/input/FilesList';

import {
  CARTOGRAPHER_ACCEPT_EXTENSIONS,
  CARTOGRAPHER_ALLOWED_MIME_TYPES,
} from '../../../constants';
import CartographerCharacterLibrary from '..';

const UPLOAD_PANEL_TAB = 'upload';
const UPLOAD_ACTIVITY_TAB = 'activity';
const UPLOAD_OPEN_DELAY_MS = 200;

export type CartographerCharacterUploadPanelTab =
  | typeof UPLOAD_PANEL_TAB
  | typeof UPLOAD_ACTIVITY_TAB;

const CHARACTER_UPLOAD_DOCS_URL =
  'https://docs.getcartwheel.com/docs/custom-characters/upload#upload-guidelines';

export interface CartographerCharacterPanelProps {
  apiKey: string | null;
  characters: ExternalCharacterListItem[];
  selectedCharacterId?: string | null;
  charactersLoading: boolean;
  charactersError: string | null;
  loadingCharacterId: string | null;
  headerContent?: ReactNode;
  onSelectCharacter: (character: ExternalCharacterListItem) => void;
  onModelFileSelected: (file: File) => void;
  onViewAll: () => void;
  /** Lifted so upload UI survives character drawer unmount (panel presence). */
  uploadPanelOpen: boolean;
  onUploadPanelOpenChange: (open: boolean) => void;
  uploadPanelTab: CartographerCharacterUploadPanelTab;
  onUploadPanelTabChange: (tab: CartographerCharacterUploadPanelTab) => void;
  /** While opening animation runs, parent can avoid closing the drawer (uploadPanelOpen is still false). */
  onUploadPanelPreparingChange: (preparing: boolean) => void;
}

interface UploadRowProps {
  file: FilesListFile;
  onRemove: () => void;
}

function UploadRow({ file, onRemove }: UploadRowProps) {
  return (
    <div className="group flex h-10 items-center gap-2 rounded-lg px-2">
      <Text variant="studio/body/medium/sm" className="min-w-0 flex-1 truncate">
        {file.file.name}
      </Text>
      <Button
        variant="tertiary"
        size="sm"
        className="text-secondary opacity-0 transition-opacity group-hover:opacity-100"
        icon={<CrossIcon />}
        aria-label={`Remove ${file.file.name}`}
        onClick={onRemove}
      />
    </div>
  );
}

function formatCreatedAtLabel(createdAt: string): string {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function CartographerCharacterPanel({
  apiKey,
  characters,
  selectedCharacterId,
  charactersLoading,
  charactersError,
  loadingCharacterId,
  headerContent,
  onSelectCharacter,
  onModelFileSelected,
  onViewAll,
  uploadPanelOpen,
  onUploadPanelOpenChange,
  uploadPanelTab,
  onUploadPanelTabChange,
  onUploadPanelPreparingChange,
}: CartographerCharacterPanelProps) {
  const isCompactViewport = useMediaQuery('(max-height: 820px)');
  const [isUploadPreparingOpen, setIsUploadPreparingOpen] = useState(false);
  const uploadOpenTimeoutRef = useRef<number | null>(null);
  const filesListRef = useRef<FilesListHandle>(null);

  useEffect(() => {
    onUploadPanelPreparingChange(isUploadPreparingOpen);
  }, [isUploadPreparingOpen, onUploadPanelPreparingChange]);

  useEffect(
    () => () => {
      onUploadPanelPreparingChange(false);
    },
    [onUploadPanelPreparingChange],
  );
  const recentUploads = useMemo(
    () =>
      [...characters].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [characters],
  );

  const cancelPendingUploadOpen = useCallback(() => {
    if (uploadOpenTimeoutRef.current === null) return;
    window.clearTimeout(uploadOpenTimeoutRef.current);
    uploadOpenTimeoutRef.current = null;
  }, []);

  const openUploadPanel = useCallback(() => {
    if (uploadPanelOpen || isUploadPreparingOpen) {
      cancelPendingUploadOpen();
      setIsUploadPreparingOpen(false);
      onUploadPanelOpenChange(false);
      return;
    }
    onUploadPanelTabChange(UPLOAD_PANEL_TAB);
    setIsUploadPreparingOpen(true);
    cancelPendingUploadOpen();
    uploadOpenTimeoutRef.current = window.setTimeout(() => {
      onUploadPanelOpenChange(true);
      setIsUploadPreparingOpen(false);
      uploadOpenTimeoutRef.current = null;
    }, UPLOAD_OPEN_DELAY_MS);
  }, [
    cancelPendingUploadOpen,
    uploadPanelOpen,
    isUploadPreparingOpen,
    onUploadPanelOpenChange,
    onUploadPanelTabChange,
  ]);

  const closeUploadPanel = useCallback(() => {
    cancelPendingUploadOpen();
    setIsUploadPreparingOpen(false);
    onUploadPanelOpenChange(false);
  }, [cancelPendingUploadOpen, onUploadPanelOpenChange]);

  useEffect(
    () => () => {
      cancelPendingUploadOpen();
    },
    [cancelPendingUploadOpen],
  );

  const handleFilesChange = useCallback(
    (files: FilesListFile[]) => {
      const nextFile = files[0]?.file;
      if (nextFile) {
        onModelFileSelected(nextFile);
        filesListRef.current?.clearAll();
      }
    },
    [onModelFileSelected],
  );
  const isUploadExpanded = uploadPanelOpen || isUploadPreparingOpen;
  const characterListMinHeightClass = isUploadExpanded
    ? isCompactViewport
      ? 'min-h-[148px]'
      : 'min-h-[172px]'
    : isCompactViewport
      ? 'min-h-[208px]'
      : 'min-h-[228px]';
  const uploadDropzoneHeight = isCompactViewport ? 128 : 148;
  const showCollapsedUploadTrigger = !uploadPanelOpen;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 lg:h-[calc(100dvh-280px)] lg:gap-6">
      {headerContent != null && (
        <div className="min-w-0 overflow-hidden">{headerContent}</div>
      )}

      <section className="flex min-h-0 flex-1 flex-col gap-2">
        <Text variant="studio/body/medium/sm">Load existing character</Text>
        <CartographerCharacterLibrary
          mode="panel"
          apiKey={apiKey}
          characters={characters}
          selectedCharacterId={selectedCharacterId}
          charactersLoading={charactersLoading}
          charactersError={charactersError}
          loadingCharacterId={loadingCharacterId}
          onSelectCharacter={onSelectCharacter}
          toolbar={{
            showSearch: true,
            showExpandButton: true,
            onExpandClick: onViewAll,
            expandTooltipText: 'Open full character library',
            expandAriaLabel: 'Open full character library',
          }}
          className={`transition-[min-height] duration-200 ${characterListMinHeightClass}`}
        />
      </section>

      <section className="flex shrink-0 flex-col gap-2 pb-1 lg:pb-2">
        <Text variant="studio/body/medium/sm">Upload &amp; Create</Text>
        {showCollapsedUploadTrigger && (
          <div className="border-gray-30 dark:border-gray-60 rounded-2xl border p-1.5 sm:p-2">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={openUploadPanel}
                className={`relative inline-flex h-10 items-center gap-2 rounded-lg px-2 text-left transition-all duration-200 ease-in-out hover:no-underline active:scale-[0.98] ${
                  isUploadExpanded
                    ? 'bg-blue text-white'
                    : 'bg-gray-15 text-gray-90 dark:bg-gray-75 dark:text-gray-15 hover:bg-gray-30 dark:hover:bg-gray-60'
                }`}
              >
                <span className="flex-none">
                  <FileIcon
                    className={isUploadExpanded ? 'text-white' : 'text-blue'}
                  />
                </span>
                <span className="min-w-0 flex-1 truncate pt-[0.1em] text-[14px]/[14px] font-medium">
                  Upload your character
                </span>
              </button>
            </div>
          </div>
        )}

        {uploadPanelOpen && (
          <div className="border-gray-30 dark:border-gray-60 rounded-2xl border bg-white px-3 py-2.5 sm:py-3 dark:bg-gray-90">
            {uploadPanelTab === UPLOAD_PANEL_TAB ? (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <Text variant="studio/title/md">Upload your character</Text>
                  <Button
                    variant="tertiary"
                    size="sm"
                    className="h-7 w-7 shrink-0"
                    icon={<CrossIcon className="h-4 w-4" />}
                    aria-label="Close upload panel"
                    onClick={closeUploadPanel}
                  />
                </div>
                <FilesList
                  ref={filesListRef}
                  allowedMimeTypes={CARTOGRAPHER_ALLOWED_MIME_TYPES}
                  fallbackExtensions={CARTOGRAPHER_ACCEPT_EXTENSIONS}
                  maxFiles={1}
                  height={uploadDropzoneHeight}
                  onFilesChange={handleFilesChange}
                  renderItem={(file, removeFile) => (
                    <UploadRow file={file} onRemove={removeFile} />
                  )}
                  emptyStateContent={
                    <div className="flex h-full items-center justify-center">
                      <div className="flex items-center gap-1.5">
                        <FileIcon className="text-blue" />
                        <Text variant="studio/body/medium/sm">
                          <span className="text-blue">Select File</span>{' '}
                          <span>or drag and drop one here</span>
                        </Text>
                      </div>
                    </div>
                  }
                  dragOverlayContent={
                    <Text variant="studio/title/xl" className="text-white">
                      Drop model to upload
                    </Text>
                  }
                />
                <Text
                  variant="studio/body/medium/xs"
                  className="text-tertiary px-2 pt-1.5 sm:pt-2"
                >
                  Supported: {CARTOGRAPHER_ACCEPT_EXTENSIONS.join(', ')}
                </Text>
                <div className="flex flex-wrap justify-between gap-y-2 px-2 pt-2.5 sm:pt-3">
                  <a
                    href={CHARACTER_UPLOAD_DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Text variant="studio/body/medium/sm" className="text-blue">
                      Upload Guidelines
                    </Text>
                  </a>
                  <button
                    type="button"
                    onClick={() => onUploadPanelTabChange(UPLOAD_ACTIVITY_TAB)}
                  >
                    <Text variant="studio/body/medium/sm" className="text-blue">
                      Upload activity
                    </Text>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2">
                  <Button
                    variant="tertiary"
                    icon={<ChevronLeftIcon />}
                    onClick={() => onUploadPanelTabChange(UPLOAD_PANEL_TAB)}
                  >
                    Upload Activity
                  </Button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    className="h-7 w-7 shrink-0"
                    icon={<CrossIcon className="h-4 w-4" />}
                    aria-label="Close upload panel"
                    onClick={closeUploadPanel}
                  />
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  {recentUploads.length === 0 && (
                    <Text variant="studio/body/md">No uploaded characters</Text>
                  )}
                  {recentUploads.map((character) => (
                    <div
                      key={character.jobID}
                      className="border-gray-30 dark:border-gray-60 rounded-lg border p-2"
                    >
                      <Text variant="studio/body/medium/sm" className="text-primary">
                        {character.characterName}
                      </Text>
                      <Text variant="studio/body/medium/xs" className="text-tertiary">
                        {formatCreatedAtLabel(character.createdAt)}
                      </Text>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
